import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * קליטת עובד חדש (PRD 5.11).
 *
 * התבנית אינה מועתקת לכל עובד: תאריך היעד של כל שלב נגזר מיום
 * התחילה שלו ומ day_offset. ככה שינוי בתבנית חל על כל מי שעדיין
 * באמצע, ואין עשרים עותקים לתחזק.
 */

export type OnboardingStep = {
  step_id: string;
  title: string;
  description: string | null;
  day_offset: number;
  due_date: string;
  course_id: string | null;
  document_id: string | null;
  completed_at: string | null;
};

export type Onboarding = {
  template: string;
  start_date: string;
  steps: OnboardingStep[];
};

export type OnboardingRow = {
  employee_id: string;
  name: string;
  branch: string | null;
  start_date: string;
  total: number;
  done: number;
};

export const ONBOARDING_KEY = ['onboarding'] as const;

export function useMyOnboarding() {
  return useQuery({
    queryKey: ONBOARDING_KEY,
    queryFn: async (): Promise<Onboarding | null> => {
      const { data, error } = await supabase.rpc('my_onboarding');
      if (error) throw error;
      return data as Onboarding | null;
    },
  });
}

export function useCompleteStep() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (stepId: string) => {
      const { error } = await supabase.rpc('complete_onboarding_step', { p_step: stepId });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ONBOARDING_KEY });
    },
  });
}

export function useOnboardingStatus() {
  return useQuery({
    queryKey: [...ONBOARDING_KEY, 'status'],
    queryFn: async (): Promise<OnboardingRow[]> => {
      const { data, error } = await supabase.rpc('onboarding_status', { p_days: 60 });
      if (error) throw error;
      return (data as OnboardingRow[] | null) ?? [];
    },
  });
}

/** האם השלב באיחור. שלב שהושלם אף פעם לא באיחור. */
export function isLate(step: OnboardingStep): boolean {
  if (step.completed_at) return false;
  return new Date(`${step.due_date}T23:59:59`) < new Date();
}
